import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (body) => {
  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

// Get all fuel with pagination and filters
export const fetchFuel = createAsyncThunk(
  "fuel/fetchAll",
  async ({ page = 1, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/rental-management/fuel?page=${page}`;
      
      // Add filters to URL
      if (filters.order_number) {
        url += `&order_number=${encodeURIComponent(filters.order_number)}`;
      }
      if (filters.plate_number) {
        url += `&plate_number=${encodeURIComponent(filters.plate_number)}`;
      }
      if (filters.admin_id) {
        url += `&admin_id=${encodeURIComponent(filters.admin_id)}`;
      }
      if (filters.fuel_type_id) {
        url += `&fuel_type_id=${encodeURIComponent(filters.fuel_type_id)}`;
      }
      if (filters.created_by) {
        url += `&created_by=${encodeURIComponent(filters.created_by)}`;
      }
      
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

// Get single fuel record
export const fetchFuelRecord = createAsyncThunk(
  "fuel/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/rental-management/fuel/${id}`, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

// Add fuel
export const addFuel = createAsyncThunk(
  "fuel/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all fields
      if (formData.admin_id) data.append("admin_id", formData.admin_id);
      if (formData.date_of_filling) data.append("date_of_filling", formData.date_of_filling);
      if (formData.vehicle_id) data.append("vehicle_id", formData.vehicle_id);
      if (formData.fuel_type_id) data.append("fuel_type_id", formData.fuel_type_id);
      if (formData.meter_reading) data.append("meter_reading", formData.meter_reading);
      if (formData.cost) data.append("cost", formData.cost);
      if (formData.notes) data.append("notes", formData.notes);
      if (formData.number_of_liters) data.append("number_of_liters", formData.number_of_liters);
      if (formData.status) data.append("status", formData.status);
      
      // Append attachments array
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file) => {
          data.append("attachments[]", file);
        });
      }

      const response = await axios.post(`${BASE_URL}/rental-management/fuel`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add fuel";
      return rejectWithValue(message);
    }
  }
);

// Update fuel
export const updateFuel = createAsyncThunk(
  "fuel/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all fields
      if (formData.admin_id) data.append("admin_id", formData.admin_id);
      if (formData.date_of_filling) data.append("date_of_filling", formData.date_of_filling);
      if (formData.vehicle_id) data.append("vehicle_id", formData.vehicle_id);
      if (formData.fuel_type_id) data.append("fuel_type_id", formData.fuel_type_id);
      if (formData.meter_reading) data.append("meter_reading", formData.meter_reading);
      if (formData.cost) data.append("cost", formData.cost);
      if (formData.notes) data.append("notes", formData.notes);
      if (formData.number_of_liters) data.append("number_of_liters", formData.number_of_liters);
      if (formData.status) data.append("status", formData.status);
      
      // Append attachments array
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach((file) => {
          data.append("attachments[]", file);
        });
      }

      const response = await axios.post(`${BASE_URL}/rental-management/fuel/${id}`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update fuel";
      return rejectWithValue(message);
    }
  }
);

// Delete fuel
export const deleteFuel = createAsyncThunk(
  "fuel/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/rental-management/fuel/${id}`, {
        headers: getHeaders(),
      });
      return { id, message: response.data.message || "Deleted successfully" };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete fuel";
      return rejectWithValue(message);
    }
  }
);

// Fetch employees for dropdown (from settings/employees)
export const fetchEmployeesForDropdown = createAsyncThunk(
  "fuel/fetchEmployees",
  async (search = "", { rejectWithValue }) => {
    try {
      const url = search
        ? `${BASE_URL}/settings/employees?search=${encodeURIComponent(search)}`
        : `${BASE_URL}/settings/employees`;
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load employees";
      return rejectWithValue(message);
    }
  }
);

// Fetch vehicles for dropdown (from settings/vehicles)
export const fetchVehiclesForDropdown = createAsyncThunk(
  "fuel/fetchVehicles",
  async (search = "", { rejectWithValue }) => {
    try {
      const url = search
        ? `${BASE_URL}/settings/vehicles?search=${encodeURIComponent(search)}`
        : `${BASE_URL}/settings/vehicles`;
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicles";
      return rejectWithValue(message);
    }
  }
);

// Fetch fuel types
export const fetchFuelTypes = createAsyncThunk(
  "fuel/fetchFuelTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/fuel-types`, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load fuel types";
      return rejectWithValue(message);
    }
  }
);

const FuelSlice = createSlice({
  name: "fuel",
  initialState: {
    fuel: [],
    fuelRecord: null,
    pagination: null,
    employees: [],
    vehicles: [],
    fuelTypes: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all fuel
      .addCase(fetchFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fuel = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Fetch single fuel record
      .addCase(fetchFuelRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFuelRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fuelRecord = action.payload;
      })
      .addCase(fetchFuelRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Add fuel
      .addCase(addFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Added successfully";
      })
      .addCase(addFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add fuel";
      })
      // Update fuel
      .addCase(updateFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Updated successfully";
      })
      .addCase(updateFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update fuel";
      })
      // Delete fuel
      .addCase(deleteFuel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFuel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fuel = state.fuel.filter((item) => item.id !== action.payload.id);
        state.success = action.payload.message || "Deleted successfully";
      })
      .addCase(deleteFuel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to delete fuel";
      })
      // Fetch employees for dropdown
      .addCase(fetchEmployeesForDropdown.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      // Fetch vehicles for dropdown
      .addCase(fetchVehiclesForDropdown.fulfilled, (state, action) => {
        state.vehicles = action.payload;
      })
      // Fetch fuel types
      .addCase(fetchFuelTypes.fulfilled, (state, action) => {
        state.fuelTypes = action.payload;
      });
  },
});

export const { clearState } = FuelSlice.actions;
export default FuelSlice.reducer;

